import React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import {Typography, Button} from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Dialog from '@mui/material/Dialog';
import Colors from "../../components/ui/Colors";
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { PuffLoader } from "react-spinners";
import { css } from "@emotion/react";
import swal from 'sweetalert';
import { useHistory } from "react-router-dom";
import Pagination from '@mui/material/Pagination';
import { getAllManualInventories, getAllManualInventoriesPaged, createManualInventory, exportManualInventoryEntriesToCsv } from '../../utilityFunctions/WarehouseUtil';

const cssLoader = css`
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const dateOptions = {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
};

let selectedInventory;

export default function ManualInventoryList(props) {
    const {loginUser} = props;

    const history = useHistory();

    const [loading, setLoading] = React.useState(false);
    const [inventoryList, setInventoryList] = React.useState([]);
    const [currentPage, setCurrentPage] = React.useState(0);
    const [totalPages, setTotalPages] = React.useState(1);
    const [pageSize] = React.useState(30);

    React.useEffect(() => {
        loadManualInventories();
    }, []);

    const loadManualInventories = async () => {
        setLoading(true);
        
        try {
            const response = await getAllManualInventoriesPaged({ 
                page: currentPage, 
                size: pageSize 
            }, loginUser.jwtToken);
            
            if (response && response.content) {
                setInventoryList(response.content);
                setTotalPages(response.totalPages);
                renderInventoryList(response.content);
            } else {
                setInventoryList([]);
                renderInventoryList([]);
            }
        } catch (error) {
            console.error("Error loading manual inventories:", error);
            swal("Error", "No se pudieron cargar los inventarios manuales", "error");
            setInventoryList([]);
            renderInventoryList([]);
        } finally {
            setLoading(false);
        }
    };

    const [inventoryListUI, setInventoryListUI] = React.useState("");

    const renderInventoryList = (data) => {
        if (!data || data.length === 0) {
            setInventoryListUI(
                <Typography variant="body1" sx={{ marginTop: "2rem" }}>
                    No hay inventarios manuales disponibles
                </Typography>
            );
            return;
        }

        // Sort data by instantDate in descending order
        const sortedData = [...data].sort((a, b) => {
            const dateA = new Date(a.instantDate);
            const dateB = new Date(b.instantDate);
            return dateB - dateA;
        });

        setInventoryListUI(
            <List sx={{ width: "100%", marginTop: { xs: "3rem", sm: "2rem", md: "2rm" } }}>
                {sortedData.map((inventory) => {
                    const entriesCount = inventory.batchEntries ? inventory.batchEntries.length : 0;
                    const createdDate = inventory.instantDate ? new Date(inventory.instantDate) : new Date();
                    
                    return (
                        <ListItem key={inventory.id} disablePadding divider sx={{ marginBottom: "1.5rem" }}>
                            <ListItemButton onClick={() => openActionBox(inventory)}>
                                <Grid container spacing={1}>
                                    <Grid item xs={3} sm={3} md={3} sx={{ display: "flex", justifyContent: "center" }}>
                                        <Typography variant="body2">{`${new Intl.DateTimeFormat('es-CO', dateOptions).format(createdDate)}`}</Typography>
                                    </Grid>
                                    <Grid item xs={3} sm={3} md={3} sx={{ display: "flex", justifyContent: "center" }}>
                                        <Typography variant="body2">{inventory.username}</Typography>
                                    </Grid>
                                    <Grid item xs={3} sm={3} md={3} sx={{ display: "flex", justifyContent: "center" }}>
                                        <Typography variant="body2">{inventory.id}</Typography>
                                    </Grid>
                                    <Grid item xs={3} sm={3} md={3} sx={{ display: "flex", justifyContent: "center" }}>
                                        <Typography variant="body2">{entriesCount}</Typography>
                                    </Grid>
                                </Grid>
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        );
    };

    const [actionBox, setActionsBox] = React.useState(false);

    const closeActionBox = () => {
        setActionsBox(false);
    };

    const openActionBox = (inventory) => {
        selectedInventory = inventory;
        setActionsBox(true);
    };

    const viewInventoryDetails = () => {
        closeActionBox();
        history.push({
            pathname: "/warehouse/manual-inventory-entry-list",
            state: {
                manualInventory: selectedInventory
            }
        });
    };

    const addNewEntry = () => {
        closeActionBox();
        history.push({
            pathname: "/warehouse/manual-inventory-entry",
            state: {
                manualInventoryId: selectedInventory.id
            }
        });
    };

    const downloadCsv = async () => {
        closeActionBox();
        setLoading(true);
        
        try {
            await exportManualInventoryEntriesToCsv(selectedInventory.id, loginUser.jwtToken);
        } catch (error) {
            console.error("Error downloading CSV:", error);
            swal("Error", "No se pudo descargar el archivo CSV", "error");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = async (event, value) => {
        setCurrentPage(value - 1); // MUI Pagination is 1-indexed, but API is 0-indexed
        
        setLoading(true);
        
        try {
            const pageRequest = {
                page: value - 1,
                size: pageSize
            };
            
            const response = await getAllManualInventoriesPaged(pageRequest, loginUser.jwtToken);
            
            if (response && response.content) {
                setInventoryList(response.content);
                renderInventoryList(response.content);
                setTotalPages(response.totalPages);
            } else {
                setInventoryList([]);
                renderInventoryList([]);
            }
        } catch (error) {
            console.error("Error loading manual inventories page:", error);
            swal("Error", "No se pudieron cargar los inventarios manuales", "error");
            setInventoryList([]);
            renderInventoryList([]);
        } finally {
            setLoading(false);
        }
    };

    const createNewInventory = async () => {
        setLoading(true);
        try {
            const newInventory = await createManualInventory(loginUser.jwtToken);
            if (newInventory) {
                swal("Éxito", "Inventario manual creado correctamente", "success");
                // Reload the list to show the new inventory
                loadManualInventories();
            } else {
                swal("Error", "No se pudo crear el inventario manual", "error");
            }
        } catch (error) {
            console.error("Error creating manual inventory:", error);
            swal("Error", "No se pudo crear el inventario manual", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%", height: "100%" }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: { xs: "start", sm: "center" },
                    width: "90%",
                    height: "100%",
                    textAlign: "center",
                    overflowY: "scroll",
                }}
            >
                {loading ? <PuffLoader size={100} color={Colors.secondary} css={cssLoader} /> : 
                <>
                    <Grid container>
                        <Grid item xs={12} sm={12} md={12} sx={{ display: "flex", justifyContent: "center", marginTop: "1.5rem" }}>
                            <Button 
                                variant="contained" 
                                onClick={createNewInventory}
                                sx={{ backgroundColor: Colors.primary, marginBottom: "1rem" }}
                            >
                                Crear Nuevo Inventario Manual
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <Grid container sx={{ marginTop: "1.5rem" }}>
                                <Grid item xs={3} sm={3} md={3} sx={{ display: "flex", justifyContent: "center" }}>
                                    <Typography variant="body1" sx={{ color: "black", fontWeight: "bold", textDecoration: 'underline' }}>Fecha de Creación</Typography>
                                </Grid>
                                <Grid item xs={3} sm={3} md={3} sx={{ display: "flex", justifyContent: "center" }}>
                                    <Typography variant="body1" sx={{ color: "black", fontWeight: "bold", textDecoration: 'underline' }}>Usuario</Typography>
                                </Grid>
                                <Grid item xs={3} sm={3} md={3} sx={{ display: "flex", justifyContent: "center" }}>
                                    <Typography variant="body1" sx={{ color: "black", fontWeight: "bold", textDecoration: 'underline' }}>ID</Typography>
                                </Grid>
                                <Grid item xs={3} sm={3} md={3} sx={{ display: "flex", justifyContent: "center" }}>
                                    <Typography variant="body1" sx={{ color: "black", fontWeight: "bold", textDecoration: 'underline' }}>Cantidad de Entradas</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            {inventoryListUI}
                        </Grid>
                        
                        <Grid item xs={12} sm={12} md={12} sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}>
                            <Pagination 
                                count={totalPages} 
                                page={currentPage + 1} 
                                onChange={handlePageChange} 
                                color="primary" 
                            />
                        </Grid>
                    </Grid>

                    <Dialog open={actionBox} onClose={closeActionBox}
                        sx={{
                            "& .MuiDialog-container": {
                                "& .MuiPaper-root": {
                                    width: { xs: "80%", sm: "40%", md: "30%" },
                                },
                            },
                        }}
                    >
                        <List sx={{ width: "100%" }}>
                            <ListItem disablePadding divider sx={{ marginBottom: "0.2rem", marginTop: "0.5rem" }}>
                                <ListItemButton onClick={viewInventoryDetails}>
                                    <Grid container>
                                        <Grid item xs={2} sm={2} md={2} sx={{ display: "flex", justifyContent: "center" }}>
                                            <FeedOutlinedIcon style={{ fontSize: "2rem" }} />
                                        </Grid>
                                        <Grid item xs={9} sm={9} md={9} sx={{ display: "flex", justifyContent: "start" }}>
                                            <Typography variant="h6">Ver Detalles</Typography>
                                        </Grid>
                                    </Grid>
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding divider sx={{ marginBottom: "0.2rem", marginTop: "0.2rem" }}>
                                <ListItemButton onClick={addNewEntry} sx={{ textAlign: "center" }}>
                                    <Grid container>
                                        <Grid item xs={2} sm={2} md={2} sx={{ display: "flex", justifyContent: "center" }}>
                                            <AddCircleOutlineIcon style={{ fontSize: "2rem" }} />
                                        </Grid>
                                        <Grid item xs={9} sm={9} md={9} sx={{ display: "flex", justifyContent: "start" }}>
                                            <Typography variant="h6">Agregar Nueva Entrada</Typography>
                                        </Grid>
                                    </Grid>
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding sx={{ marginBottom: "0.5rem", marginTop: "0.2rem" }}>
                                <ListItemButton onClick={downloadCsv} sx={{ textAlign: "center" }}>
                                    <Grid container>
                                        <Grid item xs={2} sm={2} md={2} sx={{ display: "flex", justifyContent: "center" }}>
                                            <FileDownloadIcon style={{ fontSize: "2rem" }} />
                                        </Grid>
                                        <Grid item xs={9} sm={9} md={9} sx={{ display: "flex", justifyContent: "start" }}>
                                            <Typography variant="h6">Descargar CSV</Typography>
                                        </Grid>
                                    </Grid>
                                </ListItemButton>
                            </ListItem>
                        </List>
                    </Dialog>
                </>
                }
            </Box>
        </Box>
    );
} 